import styled from "@emotion/styled/macro";

/* ═══════════════════════════════════════════════════════════════
   GBVScreeningStyles — Styled components for
   GBV Screening & Management Module (Task 20).
   Theme: Deep Teal / Slate (#37474f) with Purple accent (#6a1b9a)
   Deliberately understated to respect the sensitive nature of GBV.
   ═══════════════════════════════════════════════════════════════ */

const PRIMARY = "#455a64";
const PRIMARY_LIGHT = "#607d8b";
const PRIMARY_BG = "#eceff1";
const SECONDARY = "#37474f";
const ACCENT = "#6a1b9a";
const SUCCESS = "#2e7d32";
const WARNING = "#f57f17";
const DANGER = "#c62828";
const INFO = "#0277bd";

/* ─── Page Wrapper ─── */
export const GBVPageWrapper = styled("div")({
    padding: "16px 20px 32px",
    background: "#f5f6fa",
    minHeight: "calc(100vh - 120px)",
    "@media(max-width: 768px)": { padding: "10px 8px 24px" },
});

/* ─── Header ─── */
export const GBVHeader = styled("div")({
    background: "linear-gradient(135deg, " + PRIMARY + " 0%, " + PRIMARY_LIGHT + " 100%)",
    borderRadius: "12px",
    padding: "20px 24px",
    marginBottom: "16px",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    "@media(max-width: 768px)": { padding: "14px 16px", flexDirection: "column", alignItems: "flex-start" },
});

export const GBVHeaderLeft = styled("div")({ display: "flex", flexDirection: "column", gap: "4px" });
export const GBVHeaderTitle = styled("h2")({ margin: 0, fontSize: "1.4rem", fontWeight: 700, color: "#fff" });
export const GBVHeaderSub = styled("p")({ margin: 0, fontSize: "0.85rem", opacity: 0.85 });

export const GBVHeaderStats = styled("div")({ display: "flex", gap: "10px", flexWrap: "wrap" });

export const GBVStatChip = styled("div")(function (props) {
    let bg = "rgba(255,255,255,0.15)";
    let border = "rgba(255,255,255,0.3)";
    if (props.variant === "success") { bg = "rgba(46,125,50,0.3)"; border = "rgba(46,125,50,0.5)"; }
    if (props.variant === "warning") { bg = "rgba(245,127,23,0.3)"; border = "rgba(245,127,23,0.5)"; }
    if (props.variant === "danger") { bg = "rgba(198,40,40,0.3)"; border = "rgba(198,40,40,0.5)"; }
    if (props.variant === "info") { bg = "rgba(2,119,189,0.3)"; border = "rgba(2,119,189,0.5)"; }
    return {
        padding: "6px 14px",
        borderRadius: "20px",
        fontSize: "0.78rem",
        fontWeight: 600,
        background: bg,
        border: "1px solid " + border,
        color: "#fff",
        whiteSpace: "nowrap",
    };
});

/* ─── Confidentiality Banner ─── */
export const ConfidentialityBanner = styled("div")({
    display: "flex",
    gap: "12px",
    alignItems: "center",
    padding: "12px 18px",
    borderRadius: "8px",
    background: "#e8eaf6",
    border: "1px solid #9fa8da",
    marginBottom: "16px",
    fontSize: "0.82rem",
    color: "#283593",
    fontWeight: 500,
});

export const ConfidentialityIcon = styled("span")({
    fontSize: "1.4rem",
    lineHeight: 1,
});

/* ─── Tab Bar ─── */
export const GBVTabBar = styled("div")({
    display: "flex",
    gap: "4px",
    background: "#fff",
    borderRadius: "10px",
    padding: "4px",
    marginBottom: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflowX: "auto",
    "&::-webkit-scrollbar": { height: 0 },
});

export const GBVTabBtn = styled("button")(function (props) {
    const activeBg = props.color || PRIMARY;
    return {
        flex: "1 1 auto",
        padding: "10px 18px",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "0.82rem",
        fontWeight: props.active ? 700 : 500,
        background: props.active ? activeBg : "transparent",
        color: props.active ? "#fff" : "#555",
        transition: "all 0.2s",
        whiteSpace: "nowrap",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        ":hover": { background: props.active ? activeBg : "#f5f5f5" },
    };
});

export const GBVTabBadge = styled("span")(function (props) {
    return {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "20px",
        height: "20px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 700,
        padding: "0 6px",
        background: props.active ? "rgba(255,255,255,0.25)" : "#e0e0e0",
        color: props.active ? "#fff" : "#666",
    };
});

/* ─── Section Card ─── */
export const GBVSectionCard = styled("div")({
    background: "#fff",
    borderRadius: "10px",
    marginBottom: "16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflow: "hidden",
});

export const GBVSectionHeader = styled("div")(function (props) {
    const color = props.color || PRIMARY;
    return {
        padding: "12px 18px",
        fontWeight: 700,
        fontSize: "0.88rem",
        color: "#fff",
        background: color,
        display: "flex",
        alignItems: "center",
        gap: "8px",
    };
});

export const GBVSectionBody = styled("div")({
    padding: "16px 18px",
});

/* ─── Form Elements ─── */
export const GBVFormRow = styled("div")(function (props) {
    return {
        display: "grid",
        gridTemplateColumns: props.cols || "1fr 1fr",
        gap: "14px",
        marginBottom: "14px",
        "@media(max-width: 768px)": { gridTemplateColumns: "1fr" },
    };
});

export const GBVFormGroup = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    marginBottom: "12px",
});

export const GBVLabel = styled("label")({
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#444",
    display: "flex",
    alignItems: "center",
    gap: "4px",
});

export const GBVRequired = styled("span")({
    color: DANGER,
    fontWeight: 700,
});

export const GBVInput = styled("input")({
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "0.84rem",
    outline: "none",
    transition: "border-color 0.2s",
    ":focus": { borderColor: PRIMARY },
    "::placeholder": { color: "#bbb" },
});

export const GBVSelect = styled("select")({
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "0.84rem",
    outline: "none",
    background: "#fff",
    cursor: "pointer",
    ":focus": { borderColor: PRIMARY },
});

export const GBVTextarea = styled("textarea")({
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "0.84rem",
    outline: "none",
    minHeight: "80px",
    resize: "vertical",
    fontFamily: "inherit",
    ":focus": { borderColor: PRIMARY },
    "::placeholder": { color: "#bbb" },
});

/* ─── GBV Type Selection Cards ─── */
export const GBVTypeGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "14px",
    "@media(max-width: 768px)": { gridTemplateColumns: "1fr" },
});

export const GBVTypeCard = styled("button")(function (props) {
    const color = props.selected ? ACCENT : "#ccc";
    return {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "12px 16px",
        border: props.selected ? "2px solid " + ACCENT : "1px solid #e0e0e0",
        borderRadius: "10px",
        background: props.selected ? ACCENT + "0a" : "#fafafa",
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.2s",
        ":hover": { borderColor: ACCENT, background: ACCENT + "08" },
    };
});

export const GBVTypeIcon = styled("span")({
    fontSize: "1.5rem",
    lineHeight: 1,
});

export const GBVTypeContent = styled("div")({
    flex: 1,
});

export const GBVTypeLabel = styled("div")(function (props) {
    return {
        fontSize: "0.85rem",
        fontWeight: props.selected ? 700 : 500,
        color: props.selected ? ACCENT : "#555",
    };
});

export const GBVTypeDesc = styled("div")({
    fontSize: "0.73rem",
    color: "#888",
    marginTop: "2px",
});

/* ─── Clinical Management Checklist ─── */
export const CMGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "8px",
    marginBottom: "14px",
    "@media(max-width: 768px)": { gridTemplateColumns: "1fr 1fr" },
    "@media(max-width: 480px)": { gridTemplateColumns: "1fr" },
});

export const CMItem = styled("label")(function (props) {
    const baseColor = props.checked ? SUCCESS : "#999";
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "10px 14px",
        borderRadius: "8px",
        border: props.checked ? "2px solid " + SUCCESS : "1px solid #e0e0e0",
        background: props.checked ? SUCCESS + "0a" : "#fafafa",
        cursor: "pointer",
        fontSize: "0.8rem",
        fontWeight: props.checked ? 600 : 400,
        color: props.checked ? SUCCESS : "#555",
        transition: "all 0.2s",
        ":hover": { borderColor: SUCCESS, background: SUCCESS + "08" },
    };
});

export const CMCheckBox = styled("input")({
    accentColor: SUCCESS,
    width: "16px",
    height: "16px",
    cursor: "pointer",
});

/* ─── Injury Documentation ─── */
export const InjuryCard = styled("div")({
    background: "#fafafa",
    border: "1px solid #e8e8e8",
    borderRadius: "8px",
    padding: "14px",
    marginBottom: "10px",
    position: "relative",
});

export const InjuryHeader = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
});

export const InjuryNumber = styled("span")({
    fontSize: "0.78rem",
    fontWeight: 700,
    color: PRIMARY,
});

export const InjuryRemoveBtn = styled("button")({
    padding: "4px 10px",
    border: "none",
    borderRadius: "4px",
    background: DANGER + "15",
    color: DANGER,
    cursor: "pointer",
    fontSize: "0.72rem",
    fontWeight: 600,
    ":hover": { background: DANGER + "25" },
});

export const AddInjuryBtn = styled("button")({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "10px 16px",
    border: "2px dashed " + PRIMARY + "40",
    borderRadius: "8px",
    background: "transparent",
    color: PRIMARY,
    cursor: "pointer",
    fontSize: "0.82rem",
    fontWeight: 600,
    width: "100%",
    justifyContent: "center",
    transition: "all 0.2s",
    ":hover": { borderColor: PRIMARY, background: PRIMARY + "08" },
});

/* ─── Referral Tracking ─── */
export const ReferralGrid = styled("div")({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    marginBottom: "14px",
    "@media(max-width: 768px)": { gridTemplateColumns: "1fr" },
});

export const ReferralCard = styled("div")(function (props) {
    const color = props.active ? INFO : "#999";
    return {
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        padding: "12px 16px",
        border: props.active ? "2px solid " + INFO : "1px solid #e0e0e0",
        borderRadius: "10px",
        background: props.active ? INFO + "08" : "#fafafa",
        transition: "all 0.2s",
    };
});

export const ReferralIcon = styled("span")({
    fontSize: "1.3rem",
    lineHeight: 1,
    marginTop: "2px",
});

export const ReferralContent = styled("div")({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
});

export const ReferralLabel = styled("div")(function (props) {
    return {
        fontSize: "0.82rem",
        fontWeight: 600,
        color: props.active ? INFO : "#555",
        display: "flex",
        alignItems: "center",
        gap: "6px",
    };
});

export const ReferralToggle = styled("label")({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    fontSize: "0.78rem",
    color: "#666",
});

/* ─── Consent / Safety Plan ─── */
export const ConsentCard = styled("div")(function (props) {
    const borderColor = props.checked ? SUCCESS : WARNING;
    const bgColor = props.checked ? SUCCESS + "08" : WARNING + "08";
    return {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "14px 18px",
        border: "2px solid " + borderColor,
        borderRadius: "10px",
        background: bgColor,
        marginBottom: "10px",
        cursor: "pointer",
    };
});

export const ConsentIcon = styled("span")(function (props) {
    return {
        fontSize: "1.4rem",
        lineHeight: 1,
        color: props.checked ? SUCCESS : WARNING,
    };
});

export const ConsentLabel = styled("div")(function (props) {
    return {
        flex: 1,
        fontSize: "0.85rem",
        fontWeight: 600,
        color: props.checked ? SUCCESS : "#555",
    };
});

export const ConsentDesc = styled("div")({
    fontSize: "0.75rem",
    color: "#777",
    marginTop: "2px",
    fontWeight: 400,
});

/* ─── Alert Banners ─── */
export const GBVAlertBanner = styled("div")(function (props) {
    const colors = {
        critical: { bg: "#fce4ec", border: "#ef9a9a", icon: DANGER },
        warning: { bg: "#fff8e1", border: "#ffe082", icon: WARNING },
        info: { bg: "#e3f2fd", border: "#90caf9", icon: INFO },
        success: { bg: "#e8f5e9", border: "#a5d6a7", icon: SUCCESS },
    };
    const c = colors[props.type] || colors.info;
    return {
        display: "flex",
        gap: "12px",
        padding: "12px 16px",
        borderRadius: "8px",
        background: c.bg,
        border: "1px solid " + c.border,
        marginBottom: "12px",
        alignItems: "flex-start",
    };
});

export const GBVAlertIcon = styled("span")({ fontSize: "1.3rem", lineHeight: 1 });
export const GBVAlertContent = styled("div")({ flex: 1, display: "flex", flexDirection: "column", gap: "2px" });
export const GBVAlertTitle = styled("span")({ fontWeight: 700, fontSize: "0.82rem", color: "#333" });
export const GBVAlertDetail = styled("span")({ fontSize: "0.78rem", color: "#555" });

/* ─── Action Bar ─── */
export const GBVActionBar = styled("div")({
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    padding: "16px 0",
    flexWrap: "wrap",
});

export const GBVPrimaryBtn = styled("button")(function (props) {
    return {
        padding: "10px 24px",
        borderRadius: "8px",
        border: "none",
        background: props.disabled ? "#bbb" : PRIMARY,
        color: "#fff",
        fontWeight: 600,
        fontSize: "0.84rem",
        cursor: props.disabled ? "not-allowed" : "pointer",
        transition: "background 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        ":hover": { background: props.disabled ? "#bbb" : PRIMARY_LIGHT },
    };
});

export const GBVSecondaryBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    background: "#fff",
    color: "#555",
    fontWeight: 500,
    fontSize: "0.84rem",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    ":hover": { background: "#f5f5f5", borderColor: "#bbb" },
});

export const GBVDangerBtn = styled("button")({
    padding: "10px 24px",
    borderRadius: "8px",
    border: "none",
    background: DANGER,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.84rem",
    cursor: "pointer",
    ":hover": { background: "#b71c1c" },
});

/* ─── Timeline Styles ─── */
export const GBVTimelineContainer = styled("div")({
    position: "relative",
    paddingLeft: "24px",
    "&::before": {
        content: '""',
        position: "absolute",
        left: "10px",
        top: "0",
        bottom: "0",
        width: "2px",
        background: PRIMARY + "40",
    },
});

export const GBVTimelineItem = styled("div")(function (props) {
    let dotColor = PRIMARY;
    if (props.gbvType === "physical") dotColor = "#e65100";
    if (props.gbvType === "sexual") dotColor = DANGER;
    if (props.gbvType === "emotional") dotColor = ACCENT;
    if (props.gbvType === "economic") dotColor = WARNING;
    if (props.gbvType === "neglect") dotColor = "#795548";
    return {
        position: "relative",
        marginBottom: "20px",
        "&::before": {
            content: '""',
            position: "absolute",
            left: "-18px",
            top: "16px",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: dotColor,
            border: "2px solid " + dotColor + "30",
        },
    };
});

export const GBVTimelineCard = styled("div")({
    background: "#fff",
    border: "1px solid #e8e8e8",
    borderRadius: "10px",
    overflow: "hidden",
    transition: "box-shadow 0.2s",
    ":hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
});

export const GBVTimelineHeader = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    background: "#f9f9f9",
    borderBottom: "1px solid #eee",
    flexWrap: "wrap",
    gap: "8px",
    cursor: "pointer",
});

export const GBVTimelineDate = styled("span")({
    fontSize: "0.8rem",
    fontWeight: 600,
    color: PRIMARY,
});

export const GBVTimelineType = styled("span")(function (props) {
    let color = SECONDARY;
    if (props.gbvType === "physical") color = "#e65100";
    if (props.gbvType === "sexual") color = DANGER;
    if (props.gbvType === "emotional") color = ACCENT;
    if (props.gbvType === "economic") color = WARNING;
    if (props.gbvType === "neglect") color = "#795548";
    return {
        fontSize: "0.82rem",
        fontWeight: 700,
        color: color,
    };
});

export const GBVTimelineBody = styled("div")(function (props) {
    return {
        padding: props.open ? "12px 16px" : "0 16px",
        maxHeight: props.open ? "3000px" : "0",
        overflow: "hidden",
        transition: "all 0.3s ease",
    };
});

export const GBVTimelineField = styled("div")({
    display: "flex",
    gap: "8px",
    marginBottom: "6px",
    fontSize: "0.8rem",
});

export const GBVTimelineFieldLabel = styled("span")({
    fontWeight: 600,
    color: "#666",
    minWidth: "160px",
});

export const GBVTimelineFieldValue = styled("span")({
    color: "#333",
    flex: 1,
});

export const GBVTimelineActions = styled("div")({
    display: "flex",
    gap: "8px",
    padding: "10px 16px",
    borderTop: "1px solid #eee",
});

export const GBVTimelineBtn = styled("button")(function (props) {
    const color = props.color || PRIMARY;
    return {
        padding: "6px 14px",
        borderRadius: "6px",
        border: "1px solid " + color + "40",
        background: color + "0a",
        color: color,
        cursor: "pointer",
        fontSize: "0.76rem",
        fontWeight: 600,
        transition: "all 0.15s",
        ":hover": { background: color + "18" },
    };
});

/* ─── Empty State ─── */
export const GBVEmptyState = styled("div")({
    textAlign: "center",
    padding: "40px 20px",
});

export const GBVEmptyIcon = styled("div")({
    fontSize: "3rem",
    marginBottom: "12px",
});

export const GBVEmptyTitle = styled("h3")({
    margin: "0 0 6px",
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#555",
});

export const GBVEmptyDesc = styled("p")({
    margin: 0,
    fontSize: "0.85rem",
    color: "#888",
});

/* ─── Status Badge ─── */
export const GBVStatusBadge = styled("span")(function (props) {
    const color = props.color || PRIMARY;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "0.72rem",
        fontWeight: 600,
        background: color + "15",
        color: color,
        border: "1px solid " + color + "30",
    };
});

/* ─── Tag Pill ─── */
export const GBVTagPill = styled("span")(function (props) {
    const color = props.color || SECONDARY;
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: "10px",
        fontSize: "0.7rem",
        fontWeight: 600,
        background: color + "12",
        color: color,
    };
});

/* ─── Validation Error ─── */
export const GBVFieldError = styled("span")({
    fontSize: "0.72rem",
    color: DANGER,
    fontWeight: 500,
    marginTop: "2px",
});

/* ─── Loading ─── */
export const GBVLoading = styled("div")({
    textAlign: "center",
    padding: "60px 20px",
    fontSize: "1rem",
    color: "#888",
});

/* ─── Consent Dialog Overlay ─── */
export const ConsentDialogOverlay = styled("div")({
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
});

export const ConsentDialogBox = styled("div")({
    background: "#fff",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "480px",
    width: "90%",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
});

export const ConsentDialogTitle = styled("h3")({
    margin: "0 0 12px",
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: "8px",
});

export const ConsentDialogText = styled("p")({
    margin: "0 0 20px",
    fontSize: "0.85rem",
    color: "#555",
    lineHeight: 1.5,
});

export const ConsentDialogActions = styled("div")({
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
});

/* ─── Referral Details Input ─── */
export const ReferralDetailsInput = styled("input")({
    padding: "6px 10px",
    border: "1px solid #ddd",
    borderRadius: "5px",
    fontSize: "0.78rem",
    outline: "none",
    width: "100%",
    marginTop: "4px",
    ":focus": { borderColor: INFO },
    "::placeholder": { color: "#bbb" },
});

/* ─── PF3 Section ─── */
export const PF3Card = styled("div")(function (props) {
    const checked = props.checked;
    return {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "14px 18px",
        border: "2px solid " + (checked ? "#e65100" : "#e0e0e0"),
        borderRadius: "10px",
        background: checked ? "#fff3e0" : "#fafafa",
        marginBottom: "10px",
        cursor: "pointer",
        transition: "all 0.2s",
    };
});

export const PF3Icon = styled("span")({
    fontSize: "1.3rem",
    lineHeight: 1,
});

export const PF3Label = styled("div")({
    flex: 1,
});

export const PF3Title = styled("div")(function (props) {
    return {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: props.checked ? "#e65100" : "#555",
    };
});

export const PF3Desc = styled("div")({
    fontSize: "0.73rem",
    color: "#888",
    marginTop: "2px",
});

/* ─── Referral Status in Timeline ─── */
export const ReferralStatusGrid = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "6px",
});
